import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, dashboardPathForRole } from "@/context/AuthContext";

// The Emergent redirect URL is `${origin}/auth/callback?role=<role>#session_id=<id>`.
// We synchronously exchange the session_id server-side, then navigate to the
// role-appropriate dashboard. useRef guards against React StrictMode double-invoke.

export default function AuthCallback() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { signIn } = useAuth();
    const [error, setError] = useState(null);
    const hasProcessed = useRef(false);

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const hash = window.location.hash || "";
        const match = hash.match(/session_id=([^&]+)/);
        const sessionId = match ? decodeURIComponent(match[1]) : null;
        const role = params.get("role") === "affiliate" ? "affiliate" : "student";

        if (!sessionId) {
            setError("Missing session_id. Please try signing in again.");
            return;
        }

        (async () => {
            try {
                const res = await api.googleSession(sessionId, role);
                signIn(res.user);
                // Clean the URL hash so refreshing won't retry.
                window.history.replaceState(null, "", window.location.pathname);
                navigate(dashboardPathForRole(res.user.role), { replace: true });
            } catch (err) {
                setError(err.message || "Google sign-in failed.");
            }
        })();
    }, [navigate, params, signIn]);

    return (
        <div
            className="min-h-screen flex items-center justify-center px-6"
            data-testid="auth-callback"
            style={{ background: "var(--st-white)" }}
        >
            <div className="st-surface p-10 max-w-md w-full text-center">
                <div
                    className="tracking-caps text-[11px] font-semibold"
                    style={{ color: "var(--st-royal)" }}
                >
                    Completing sign-in
                </div>
                <div
                    className="font-serif text-2xl mt-3"
                    style={{ color: "var(--st-navy)" }}
                >
                    {error ? "We hit a snag" : "One moment…"}
                </div>
                {error ? (
                    <>
                        <p className="mt-3 text-sm" style={{ color: "#B42318" }} data-testid="auth-callback-error">
                            {error}
                        </p>
                        <button
                            className="btn-outline-navy mt-6"
                            onClick={() => navigate("/login", { replace: true })}
                            data-testid="auth-callback-retry-btn"
                        >
                            Back to sign in
                        </button>
                    </>
                ) : (
                    <p className="mt-3 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                        Verifying your Google session with SwingTrust.
                    </p>
                )}
            </div>
        </div>
    );
}
